/**
 * "Chi bất thường" — the §10 report (Phase 1, step 1.4 remainder).
 *
 * Spec §10 asks one question: how much cash should the household be holding back each month?
 * The answer comes from history, so this reads whole months back rather than the month on
 * screen — one bad month says nothing, a year says what this family is actually like.
 *
 * Only spending somebody MARKED as unexpected or emergency is counted. The category-group
 * default (N11 hướng A) never produces those two values, so nothing here is inflated by a
 * planned holiday that happened to sit in "Chi phí phát sinh".
 */
import { supabase } from '../../../shared/api/supabase/client.js';
import { appErrorException } from '../../../shared/lib/errors.js';

/** A year of history: long enough to include the once-a-year surprises. */
export const UNEXPECTED_MONTHS = 12;

export type UnexpectedRow = Readonly<{
  /** First day of the month, YYYY-MM-DD in the household timezone. */
  periodStart: string;
  categoryId: string | null;
  categoryName: string | null;
  planningNature: 'unexpected' | 'emergency';
  amount: number;
}>;

export async function fetchUnexpected(
  subjectId: string,
  months = UNEXPECTED_MONTHS,
): Promise<readonly UnexpectedRow[]> {
  const { data, error } = await supabase
    .schema('app')
    .rpc('unexpected_expenses', { p_subject_id: subjectId, p_months: months });
  if (error) throw appErrorException(error);

  return (data ?? []).map((row) => ({
    periodStart: row.period_start ?? '',
    categoryId: row.category_id,
    categoryName: row.category_name,
    planningNature: (row.planning_nature ?? 'unexpected') as 'unexpected' | 'emergency',
    amount: Number(row.amount ?? 0),
  }));
}
